'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2DiffPreferences } from '@/lib/stores/pr-v2-view-preferences-store';
import { SlidersHorizontal } from 'lucide-react';
import { useMemo } from 'react';
import type { Command, CommandGroupName } from '../types';
import { COMMAND_GROUPS } from '../types';
import { prV2Command } from './pr-v2-command-group';
import { useIsMobile } from '@/hooks/use-mobile';
import { isPrV2Route } from '@/lib/pr-v2/route-utils';
import { PR_V2_DIFF_INDICATOR_OPTIONS, PR_V2_DIFF_STYLE_OPTIONS, PR_V2_HUNK_SEPARATOR_OPTIONS, PR_V2_LINE_DIFF_OPTIONS, PR_V2_OVERFLOW_OPTIONS, usePrV2ViewPreferencesStore } from '@/lib/stores/pr-v2-view-preferences-store';
type ChoiceKey = 'diffStyle' | 'overflow' | 'diffIndicators' | 'lineDiffType' | 'hunkSeparators';
type ToggleKey = 'hideTestFiles' | 'showLineNumbers' | 'expandUnchanged';
type ApplyPreferences = (next: Partial<PrV2DiffPreferences>) => void;
function toLabel(value: string): string {
  const sentence = value.replaceAll('-', ' ');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

// The group name is a keyword, so a search from the root list for "layout split" finds the row.
function optionRow(row: Pick<Command, 'id' | 'label' | 'action' | 'checked'> & {
  group: CommandGroupName;
}): Command {
  return {
    ...row,
    keywords: ['diff', row.group.toLowerCase()],
    keepOpen: true,
    showOn: isPrV2Route,
    tag: 'pr-v2-diff-view'
  };
}
function choiceRows<K extends ChoiceKey>({
  key,
  group,
  options,
  current,
  applyPreferences
}: {
  key: K;
  group: CommandGroupName;
  options: readonly PrV2DiffPreferences[K][];
  current: PrV2DiffPreferences[K];
  applyPreferences: ApplyPreferences;
}): Command[] {
  return options.map(option => optionRow({
    id: `pr-v2-diff-view:${key}:${option}`,
    label: toLabel(option),
    group,
    checked: option === current,
    action: () => {
      const next: Partial<PrV2DiffPreferences> = {};
      next[key] = option;
      applyPreferences(next);
    }
  }));
}
function toggleRow({
  key,
  label,
  checked,
  applyPreferences
}: {
  key: ToggleKey;
  label: string;
  checked: boolean;
  applyPreferences: ApplyPreferences;
}): Command {
  return optionRow({
    id: `pr-v2-diff-view:${key}`,
    label,
    group: COMMAND_GROUPS.DIFF_FILES.name,
    checked,
    action: () => {
      // Read when run, so a second quick toggle flips the value the first one set.
      const next: Partial<PrV2DiffPreferences> = {};
      next[key] = !usePrV2ViewPreferencesStore.getState()[key];
      applyPreferences(next);
    }
  });
}

/**
 * The "Diff view..." command and the rows of its mode: one row per diff setting value, checked
 * when it is the current one.
 */
export function usePrV2DiffViewCommands() {
  const $ = _c(25);
  const isMobile = useIsMobile();
  const diffStyle = usePrV2ViewPreferencesStore(_temp);
  const overflow = usePrV2ViewPreferencesStore(_temp2);
  const diffIndicators = usePrV2ViewPreferencesStore(_temp3);
  const lineDiffType = usePrV2ViewPreferencesStore(_temp4);
  const hunkSeparators = usePrV2ViewPreferencesStore(_temp5);
  const hideTestFiles = usePrV2ViewPreferencesStore(_temp6);
  const showLineNumbers = usePrV2ViewPreferencesStore(_temp7);
  const expandUnchanged = usePrV2ViewPreferencesStore(_temp8);
  const applyPreferences = usePrV2ViewPreferencesStore(_temp9);
  const resetDiffPreferences = usePrV2ViewPreferencesStore(_temp10);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = prV2Command("pr-v2-diff-view", {
      label: "Diff view...",
      icon: <SlidersHorizontal />,
      action: _temp11,
      enterMode: "pr-v2-diff-view",
      keepOpen: true,
      keywords: ["diff", "settings", "display", "preferences", "layout"]
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== applyPreferences || $[2] !== diffIndicators || $[3] !== diffStyle || $[4] !== expandUnchanged || $[5] !== hideTestFiles || $[6] !== hunkSeparators || $[7] !== isMobile || $[8] !== lineDiffType || $[9] !== overflow || $[10] !== resetDiffPreferences || $[11] !== showLineNumbers) {
    const t2 = choiceRows({
      key: "overflow",
      group: COMMAND_GROUPS.DIFF_OVERFLOW.name,
      options: PR_V2_OVERFLOW_OPTIONS,
      current: overflow,
      applyPreferences
    });
    const t3 = choiceRows({
      key: "diffIndicators",
      group: COMMAND_GROUPS.DIFF_INDICATORS.name,
      options: PR_V2_DIFF_INDICATOR_OPTIONS,
      current: diffIndicators,
      applyPreferences
    });
    const t4 = choiceRows({
      key: "lineDiffType",
      group: COMMAND_GROUPS.DIFF_INLINE.name,
      options: PR_V2_LINE_DIFF_OPTIONS,
      current: lineDiffType,
      applyPreferences
    });
    const t5 = choiceRows({
      key: "hunkSeparators",
      group: COMMAND_GROUPS.DIFF_HUNK_HEADER.name,
      options: PR_V2_HUNK_SEPARATOR_OPTIONS,
      current: hunkSeparators,
      applyPreferences
    });
    let t6;
    if ($[13] !== applyPreferences || $[14] !== hideTestFiles) {
      t6 = toggleRow({
        key: "hideTestFiles",
        label: "Hide test files",
        checked: hideTestFiles,
        applyPreferences
      });
      $[13] = applyPreferences;
      $[14] = hideTestFiles;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    let t7;
    if ($[16] !== applyPreferences || $[17] !== showLineNumbers) {
      t7 = toggleRow({
        key: "showLineNumbers",
        label: "Show line numbers",
        checked: showLineNumbers,
        applyPreferences
      });
      $[16] = applyPreferences;
      $[17] = showLineNumbers;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== applyPreferences || $[20] !== expandUnchanged) {
      t8 = toggleRow({
        key: "expandUnchanged",
        label: "Expand unchanged",
        checked: expandUnchanged,
        applyPreferences
      });
      $[19] = applyPreferences;
      $[20] = expandUnchanged;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = ["diff", "reset", "defaults", "settings"];
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    let t10;
    if ($[23] !== resetDiffPreferences) {
      t10 = {
        id: "pr-v2-diff-view:reset",
        label: "Reset diff settings to defaults",
        action: resetDiffPreferences,
        keywords: t9,
        keepOpen: true,
        showOn: isPrV2Route,
        tag: "pr-v2-diff-view"
      };
      $[23] = resetDiffPreferences;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    t1 = [t0, ...(isMobile ? [] : choiceRows({
      key: "diffStyle",
      group: COMMAND_GROUPS.DIFF_LAYOUT.name,
      options: PR_V2_DIFF_STYLE_OPTIONS,
      current: diffStyle,
      applyPreferences
    })), ...t2, ...t3, ...t4, ...t5, t6, t7, t8, t10];
    $[1] = applyPreferences;
    $[2] = diffIndicators;
    $[3] = diffStyle;
    $[4] = expandUnchanged;
    $[5] = hideTestFiles;
    $[6] = hunkSeparators;
    $[7] = isMobile;
    $[8] = lineDiffType;
    $[9] = overflow;
    $[10] = resetDiffPreferences;
    $[11] = showLineNumbers;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  return t1;
}
function _temp11() {
  return false;
}
function _temp10(state_8) {
  return state_8.resetDiffPreferences;
}
function _temp9(state_7) {
  return state_7.applyPreferences;
}
function _temp8(state_6) {
  return state_6.expandUnchanged;
}
function _temp7(state_5) {
  return state_5.showLineNumbers;
}
function _temp6(state_4) {
  return state_4.hideTestFiles;
}
function _temp5(state_3) {
  return state_3.hunkSeparators;
}
function _temp4(state_2) {
  return state_2.lineDiffType;
}
function _temp3(state_1) {
  return state_1.diffIndicators;
}
function _temp2(state_0) {
  return state_0.overflow;
}
function _temp(state) {
  return state.diffStyle;
}